import { Box } from "@primer/react";
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { hinhThucMienGiamApi } from "../../../../api/hinhThucMienGiamApi";
import { khoanNopApi } from "../../../../api/khoanNopApi";
import DataGrid from "../../../../components/data-grid/DataGrid";
import { IDataGridColumn } from "../../../../components/data-grid/type/IDataGridProps";
import { Button } from "../../../../components/buttons";
import { AnimationPopup } from "../../../../components/modal";
import { PlaceHolder } from "../../../../components/place-holder";
import { showNotify } from "../../../../helpers/toast";
import { sf_hinhthucmiengiam } from "../../../../models/response/category/sf_hinhthucmiengiam";
import { sf_hinhthucmiengiam_chitiet_viewmodel } from "../../../../models/response/category/sf_hinhthucmiengiam_chitiet";
import { RootState } from "../../../../state/reducers";

type PendingEditRow = sf_hinhthucmiengiam_chitiet_viewmodel & {
    sf_hinhthucmiengiam_chitiet_id?: number;
};

type MienGiamEditItemsModalProps = {
    sf_hinhthucmiengiam: sf_hinhthucmiengiam,
    onClose: () => void,
    onChanged: () => void,
    animationOf: string
}

export const MienGiamEditItemsModal = (props: MienGiamEditItemsModalProps) => {
    const [sf_khoannop_viewmodel_miengiams, set_sf_khoannop_viewmodel_miengiams] = useState<PendingEditRow[]>([]);
    const [pendingChanges, setPendingChanges] = useState<Record<number, PendingEditRow>>({});
    const [isSaving, SetIsSaving] = useState<boolean>(false);
    const [isLoaded, SetIsLoaded] = useState<boolean>(false);
    const { dm_coso_id } = useSelector((x: RootState) => x.common);

    useEffect(() => {
        handelReload();
    }, [props.sf_hinhthucmiengiam.id, dm_coso_id]);

    const handelReload = async () => {
        if (props.sf_hinhthucmiengiam.id > 0) {
            SetIsLoaded(false);
            let isLoading: boolean = true;
            const interval_obj = setInterval(() => {
                if (!isLoading) {
                    SetIsLoaded(true);
                    clearInterval(interval_obj);
                }
            }, 1000);
            const res = await khoanNopApi.select_by_miengiam(props.sf_hinhthucmiengiam.id);
            if (res.is_success && res.data !== undefined && res.data !== null) {
                const dataSource = res.data
                    .filter(
                        (c: any) =>
                            c.dm_coso_id === dm_coso_id
                            && !(c.sf_hinhthucmiengiam_chitiet_id && Number(c.sf_hinhthucmiengiam_chitiet_id) > 0)
                    )
                    .map((item: PendingEditRow) => ({
                        ...item,
                        sf_hinhthucmiengiam_chitiet_id: item.sf_hinhthucmiengiam_chitiet_id || item.id,
                    }));
                set_sf_khoannop_viewmodel_miengiams(dataSource);
            }
            else {
                set_sf_khoannop_viewmodel_miengiams([]);
            }
            setPendingChanges({});
            isLoading = false;
        } else {
            set_sf_khoannop_viewmodel_miengiams([]);
            setPendingChanges({});
            SetIsLoaded(true);
        }
    };

    const upsertPendingChange = (updatedItem: PendingEditRow) => {
        setPendingChanges((prev) => ({
            ...prev,
            [updatedItem.id]: {
                ...(prev[updatedItem.id] || updatedItem),
                ...updatedItem,
            },
        }));
    };

    const handleInlineValueChange = (
        id: number,
        field: "phan_tram_mien_giam" | "so_tien_mien_giam",
        value: string
    ) => {
        let updatedItem: PendingEditRow | null = null;

        set_sf_khoannop_viewmodel_miengiams((prev) =>
            prev.map((item) => {
                if (item.id !== id) {
                    return item;
                }

                updatedItem = {
                    ...item,
                    [field]: value === "" ? 0 : Number(value),
                };
                return updatedItem;
            })
        );

        if (updatedItem) {
            upsertPendingChange(updatedItem);
        }
    };

    const handleSaveChanges = async () => {
        const payload = Object.values(pendingChanges).map((item) => ({
            id: item.id,
            sf_hinhthucmiengiam_id: props.sf_hinhthucmiengiam.id,
            sf_hinhthucmiengiam_chitiet_id: item.sf_hinhthucmiengiam_chitiet_id,
            phan_tram_mien_giam: Number(item.phan_tram_mien_giam || 0),
            so_tien_mien_giam: Number(item.so_tien_mien_giam || 0),
        }));

        if (payload.length === 0) {
            showNotify({ message: "Không có dữ liệu thay đổi", type: "warning" });
            return;
        }

        SetIsSaving(true);
        const res = await hinhThucMienGiamApi.save_change_chitiets(payload);
        SetIsSaving(false);

        if (res.is_success) {
            showNotify({ message: "Cập nhật thành công", type: "success" });
            setPendingChanges({});
            props.onChanged();
            await handelReload();
        } else {
            showNotify({ message: "Có lỗi " + (res.message || ""), type: "error" });
        }
    };

    const onClose = useCallback(() => {
        window.animationHidePopup(props.animationOf || "");
        setTimeout(() => {
            props.onClose();
        }, 500);
    }, [props.animationOf, props.onClose]);

    const renderEditableNumberCell = useCallback((
        cellData: PendingEditRow,
        field: "phan_tram_mien_giam" | "so_tien_mien_giam"
    ) => {
        return (
            <Box sx={{ px: 1, py: 1 }}>
                <input
                    type="number"
                    value={cellData[field] ?? ""}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => {
                        handleInlineValueChange(cellData.id, field, e.target.value);
                    }}
                    style={{
                        width: "100%",
                        minWidth: "80px",
                        height: "30px",
                        padding: "0 8px",
                        border: "1px solid #d0d7de",
                        borderRadius: "6px",
                        textAlign: "right",
                        outline: "none",
                        backgroundColor: "#ffffff",
                    }}
                />
            </Box>
        );
    }, []);

    const columns = useMemo<IDataGridColumn[]>(() => [
        {
            caption: "Mã khoản nộp",
            id: "ma_khoan_nop",
            dataField: "ma_khoan_nop",
            width: 140,
        },
        {
            caption: "Tên khoản nộp",
            id: "ten_khoan_nop",
            dataField: "ten_khoan_nop",
            width: 280,
        },
        {
            caption: "% miễn giảm",
            id: "phan_tram_mien_giam",
            dataField: "phan_tram_mien_giam",
            width: 140,
            align: "right",
            cellRender: (data: PendingEditRow) => renderEditableNumberCell(data, "phan_tram_mien_giam"),
        },
        {
            caption: "Số tiền cố định",
            id: "so_tien_mien_giam",
            dataField: "so_tien_mien_giam",
            width: 160,
            align: "right",
            cellRender: (data: PendingEditRow) => renderEditableNumberCell(data, "so_tien_mien_giam"),
        },
    ], [renderEditableNumberCell]);

    const hasPendingChanges = Object.keys(pendingChanges).length > 0;

    return (
        <AnimationPopup
            width="80%"
            animationOf={props.animationOf}
            title="Điều chỉnh % miễn giảm"
            maxWidth={700}
            onClose={onClose}
        >
            <div>
                {isLoaded && <div>
                    <DataGrid
                        dataSource={sf_khoannop_viewmodel_miengiams}
                        columns={columns}
                        keyExpr="id"
                        height={window.innerHeight - 220}
                        isShowRowIdx={false}
                        searchEnable={true}
                        filterRow={{
                            enable: true,
                        }}
                        grouping={{
                            enable: true,
                            groupField: "nhom_khoan_nop",
                            defaultExpanded: true,
                        }}
                        paging={{
                            enable: false,
                        }}
                    />
                    <div className="pop-up-actions">
                        <Button type="normal" icon="fa fa-times" text="Đóng" onClick={onClose}></Button>{' '}
                        <Button
                            type="normal"
                            isLoading={isSaving}
                            isDisabled={!hasPendingChanges}
                            text="Cập nhật"
                            icon="fas fa-check-square"
                            onClick={handleSaveChanges}
                        ></Button>
                    </div>
                </div>}
                {!isLoaded && <PlaceHolder line_number={20} />}
            </div>
        </AnimationPopup>
    );
}